import { UseFormReturn, useWatch } from 'react-hook-form';
import { useFormatter, useTranslations } from 'use-intl';
import type { SetRangeFormFields } from './types';
import { formatEther, parseEther } from 'viem';
import { getLiquidityPercentage } from './utils';

type Form = UseFormReturn<SetRangeFormFields>;

export interface Props {
  poolType: 'pair' | 'eth' | 'nft';
  amountMax: bigint | undefined;
  control: Form['control'];
  using: 'vtoken' | 'nft';
  showTicks: boolean;
  setShowTicks: (v: boolean) => void;
}

export const Explainer = ({
  amountMax,
  control,
  poolType,
  using,
  showTicks,
  setShowTicks,
}: Props) => {
  // formatting
  const t = useTranslations('CreateLiquidityPositionForms.SetRange');
  const { number } = useFormatter();

  const { tokenAmount } = useWatch({
    control,
  });

  const liquiditySplit = getLiquidityPercentage(amountMax, tokenAmount);
  const inventorySplit =
    liquiditySplit !== null ? Math.max(1 - liquiditySplit, 0) : null;
  const isEthOnly = poolType === 'eth';
  const isNftOnly = poolType === 'nft';
  const isFixedAmount = isNftOnly && using === 'nft';

  return (
    <aside className="mt-4 text-sm font-medium text-gray-500">
      {liquiditySplit === null &&
        inventorySplit === null &&
        !isEthOnly &&
        using === 'nft' && (
          <p className="text-sm font-medium text-red-600 mb-2">
            {
              'Unused tokens are added as an inventory position and earn 20% of vault rewards.'
            }
          </p>
        )}
      <div className="flex justify-between">
        <p>
          {!isEthOnly &&
            using !== 'vtoken' &&
            !isFixedAmount &&
            t.rich('depositExplainer.text', {
              inventory: (
                <strong className="font-bold text-gray-800">
                  {t('depositExplainer.items', {
                    count:
                      amountMax && tokenAmount
                        ? formatEther(
                            (() => {
                              const v = amountMax - parseEther(tokenAmount);
                              if (v > 0n) {
                                return v;
                              }
                              return 0n;
                            })()
                          )
                        : 0,
                  })}
                </strong>
              ) as any,
              inventoryPercent:
                inventorySplit !== null
                  ? number(inventorySplit, {
                      style: 'percent',
                      minimumFractionDigits: 2,
                    })
                  : '--%',
              liquidity: (
                <strong className="font-bold text-gray-800">
                  {t('depositExplainer.items', {
                    count: formatEther(parseEther(tokenAmount || '0')),
                  })}
                </strong>
              ) as any,
              liquidityPercent:
                liquiditySplit !== null
                  ? number(liquiditySplit, {
                      style: 'percent',
                      minimumFractionDigits: 2,
                    })
                  : '--%',
            })}
          {isFixedAmount &&
            t('depositExplainer.nftOnly', {
              liquidity: t('depositExplainer.items', {
                count: formatEther(parseEther(tokenAmount || '0')),
              }),
            })}
          <span className="block text-xs mt-2 italic">
            {t('depositExplainer.helper')}
          </span>
        </p>
        {!showTicks && (
          <button
            type="button"
            className="justify-self-end opacity-0 hover:opacity-50"
            onClick={() => setShowTicks(true)}
          >
            show ticks
          </button>
        )}
      </div>
    </aside>
  );
};

export default Explainer;
